import React from 'react';
import { connect } from 'react-redux';

import Audio from 'components/presentational/Audio';

// Import Audio URL Paths
import all_audio from '../../../model/all-audio';

// @link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState#Value
const MEDIA_READY_STATE = {
    HAVE_NOTHING: 0,
    HAVE_METADATA: 1,
    HAVE_CURRENT_DATA: 2,
    HAVE_FUTURE_DATA: 3,
    HAVE_ENOUGH_DATA: 4,
};

const mapStateToProps = state => {
    return {
        volumeIsOn: state.volumeIsOn,
        audioToPlay: state.audioToPlay,
        audioPlayCount: state.audioPlayCount,
        audioStopCount: state.audioStopCount,
    };
};

class AllAudio extends React.Component {
    constructor(props) {
        super(props);

        this.audio_wrapper = React.createRef();
        this.audio_keys = Object.keys(all_audio);

        this.stopAudio = this.stopAudio.bind(this);
    }

    componentDidUpdate(prevProps) {
        let { audioToPlay, volumeIsOn, audioPlayCount, audioStopCount } = this.props;
        let audio_wrapper = this.audio_wrapper.current;

        if (prevProps.volumeIsOn !== volumeIsOn) {
            // Update volume / muted state
            for (let i = 0; i < audio_wrapper.children.length; i++) {
                let audio_element = audio_wrapper.children[i];
                audio_element.muted = !Boolean(this.props.volumeIsOn);
            }
        }

        if (prevProps.audioStopCount !== audioStopCount) {
            this.stopAudio();
        }

        if (prevProps.audioPlayCount !== audioPlayCount) {
            // Stop all audio files, and later play the new one
            this.stopAudio();

            let audio_element_to_play = audio_wrapper.querySelector(
                `[data-audio-key="${audioToPlay}"]`
            );

            // Uncomment the line below to view the URL of the audio file that is currently playing
            // console.log('Playing the audio file: ' + audio_element_to_play.currentSrc);

            audio_element_to_play.play();
        }
    }

    componentDidMount() {
        let { audioToPlay, volumeIsOn } = this.props;
        let audio_wrapper = this.audio_wrapper.current;

        // First, set muted state on each audio element
        for (let i = 0; i < audio_wrapper.children.length; i++) {
            let audio_element = audio_wrapper.children[i];
            audio_element.muted = Boolean(volumeIsOn);
        }

        if (audioToPlay) {
            let audio_element = audio_wrapper.querySelector(`[data-audio-key="${audioToPlay}"]`);
            if (audio_element.readyState >= MEDIA_READY_STATE.HAVE_METADATA) {
                audio_element.currentTime = 0;
            } else {
                /**
                 * If no meta data has loaded, wait for it to load before we seek to the beginning.
                 * This is mostly to prevent IE from throwing errors.
                 * @see https://stackoverflow.com/a/31390386/864233
                 */
                audio_element.addEventListener(
                    'loadedmetadata',
                    function(e) {
                        // `this` is our HTMLMediaElement
                        this.currentTime = 0;
                    },
                    false
                );
            }

            /**
             * Trigger our play event on the audio element. If the file hasn't loaded enough
             * information to play, that's OK, this function call triggers a `waiting` event
             * on the audio element, meaning it will play once it has enough data. When we do
             * have enough data, a `playing` event is fired.
             *
             * @see https://developer.mozilla.org/en-US/docs/Web/Guide/Events/Media_events
             */
            audio_element.play();
        }
    }

    stopAudio(seek_to_beginning = true) {
        let audio_wrapper = this.audio_wrapper.current;

        for (let i = 0; i < audio_wrapper.children.length; i++) {
            let audio_element = audio_wrapper.children[i];

            audio_element.pause();
            /**
             * IE complains if we try to set the time of a file we have no metadata on.
             * If we don't have the metadata, then there is nothing to pause.
             */
            if (audio_element.readyState >= MEDIA_READY_STATE.HAVE_METADATA) {
                if (seek_to_beginning) {
                    audio_element.currentTime = 0;
                }
            }
        }
    }

    render() {
        return (
            <div className="audio-wrapper" ref={this.audio_wrapper}>
                {this.audio_keys.map(audio_key => {
                    return (
                        <Audio
                            key={audio_key}
                            audioKey={audio_key}
                            mp3Src={all_audio[audio_key].mp3}
                            aifSrc={all_audio[audio_key].aif}
                        />
                    );
                })}
            </div>
        );
    }
}

export default connect(mapStateToProps)(AllAudio);
